<template>
    <button
        ref="buttonRef"
        :class="[$style.button, isPortal && $style.button_portal]"
        data-interface-target="close-game"
        :style="{ top: `${positionY}px` }"
        aria-label="Close game"
        @touchstart="startDrag"
        @click="props.onClick"
    >
        <Icon
            v-if="isPortal"
            name="chevron_l"
            :class="$style.chevron_icon"
        />
        <Icon
            :name="isPortal ? 'fennek' : 'close'"
            :class="[$style.button_icon]"
        />
    </button>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import Icon from '@/components_new/Icon/Icon.vue'

interface DragnDropCloseProps {
    startPosition?: number
    startLandscapePosition?: number
    mode: 'portal' | 'widget'
    onClick: (e: MouseEvent) => void
}

const props = withDefaults(defineProps<DragnDropCloseProps>(), {
    startPosition: 0,
})

const MIN_POSITION = 0
const buttonRef = ref<HTMLElement | null>(null)
const positionY = ref(props.startPosition)
const dragging = ref(false)
const offsetY = ref(0)
const isPortal = computed(() => props.mode === 'portal')

function startDrag(event: TouchEvent) {
    if (!buttonRef?.value) {
        return
    }
    const { clientY } = event.touches[0]
    dragging.value = true

    offsetY.value = clientY - buttonRef.value.getBoundingClientRect().top
    document.addEventListener('touchmove', onDrag)
    document.addEventListener('touchend', stopDrag)
}

function onDrag(event: TouchEvent) {
    if (!dragging.value || !buttonRef?.value) {
        return
    }
    event.preventDefault()
    const { clientY } = event.touches[0]
    const button = buttonRef.value
    const containerHeight = document.body.getBoundingClientRect().height
    const buttonHeight = button.offsetHeight
    let newY = clientY - offsetY.value

    newY = Math.max(MIN_POSITION, newY)
    newY = Math.min(containerHeight - buttonHeight, newY)

    positionY.value = newY
}

function setStartPosition() {
    const isLandscape = screen.orientation.type.startsWith('landscape')
    if (isLandscape) {
        positionY.value = props.startLandscapePosition ?? props.startPosition
    } else {
        positionY.value = props.startPosition
    }
}

function stopDrag() {
    dragging.value = false
    document.removeEventListener('touchmove', onDrag)
    document.removeEventListener('touchend', stopDrag)
}

onMounted(() => {
    window.addEventListener('orientationchange', setStartPosition)
})

onBeforeUnmount(() => {
    document.removeEventListener('touchmove', onDrag)
    document.removeEventListener('touchend', stopDrag)
    window.removeEventListener('orientationchange', setStartPosition)
})
</script>
<style module>
.button {
    touch-action: none;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0px 48px 48px 0px;
    background-color: var(--white);
    border: none;
    outline: none;
    position: absolute;
    left: 0;
    top: 10px;
    padding: 8px;
    cursor: pointer;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
}

.button_portal {
    padding-left: 28px;
}

.button_icon {
    width: 32px;
    height: 32px;
    color: var(--purple-400);
    border-radius: 32px;
}

.button_portal .button_icon {
    width: 36px;
    height: 32px;
}

.chevron_icon {
    width: 24px;
    height: 23px;
    position: absolute;
    left: 4px;
    color: var(--purple-400);
}
</style>
